import { createFileRoute, Link } from "@tanstack/react-router";
import { PublicShell } from "@/components/site-chrome";
import { Stamp } from "@/components/stamp";

export const Route = createFileRoute("/refund")({
  head: () => ({
    meta: [
      { title: "Refund & Cancellation Policy | monroya.ai" },
      {
        name: "description",
        content:
          "30-day money-back guarantee on your first Monroya payment. Cancel any time and keep access until the end of your billing period.",
      },
      { property: "og:title", content: "Refund & Cancellation Policy | monroya.ai" },
      {
        property: "og:description",
        content:
          "30-day money-back guarantee on your first payment. Cancel any time and keep access until the end of your billing period.",
      },
      { property: "og:url", content: "https://www.monroya.ai/refund" },
    ],
    links: [{ rel: "canonical", href: "https://www.monroya.ai/refund" }],
  }),
  component: RefundPage,
});

function RefundPage() {
  return (
    <PublicShell>
      <article className="site-container px-5 py-16 sm:px-6">
        <Stamp tone="slate">Legal</Stamp>
        <h1 className="mt-5 font-serif text-5xl tracking-tight text-ink">
          Refund &amp; Cancellation Policy
        </h1>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          Last updated October 5, 2026
        </p>

        <div className="mt-8 space-y-5 leading-relaxed text-ink/85">
          <p>
            We keep this simple. Monroya offers a free plan so you can
            evaluate the product before paying, and your first paid charge
            is covered by a 30-day money-back guarantee.
          </p>

          <h2 className="font-serif text-xl text-ink">30-day money-back guarantee</h2>
          <p>
            If Monroya isn't right for you, email{" "}
            <a className="underline" href="mailto:support@monroya.ai">
              support@monroya.ai
            </a>{" "}
            within <strong>30 days of your first payment</strong> on any paid
            plan, monthly or annual, and we refund that payment in full. No
            questions, no call required. The guarantee applies once per
            business.
          </p>
          <p>
            Outside that window, renewals, partial billing periods and unused
            time after cancellation are not refunded.
          </p>

          <h2 className="font-serif text-xl text-ink">Cancel any time</h2>
          <p>
            You can cancel from <strong>Settings → Billing</strong> at any
            time. Cancellation stops future renewals. Outside the 30-day
            guarantee it does not trigger a refund for the current period. You keep full access until the end
            of the billing period you've already paid for, then your account
            downgrades automatically.
          </p>

          <h2 className="font-serif text-xl text-ink">Auto-renewal</h2>
          <p>
            All Monroya subscriptions <strong>renew automatically</strong>{" "}
            until you cancel them:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Monthly plans</strong> renew every month at the same
              price on the same day of the month.
            </li>
            <li>
              <strong>Annual plans</strong> renew every 12 months on the
              anniversary of the original charge.
            </li>
          </ul>
          <p>
            Your payment method on file is charged on each renewal date. We
            don't send reminder emails before each monthly renewal; annual
            renewals get a reminder email at least 7 days in advance.
          </p>

          <h2 className="font-serif text-xl text-ink">Billing errors</h2>
          <p>
            If you believe you've been charged in error, for example, a
            duplicate charge or a charge after a confirmed cancellation, 
            email{" "}
            <a className="underline" href="mailto:support@monroya.ai">
              support@monroya.ai
            </a>{" "}
            within 30 days and we'll investigate. Confirmed errors are
            refunded to the original payment method.
          </p>

          <h2 className="font-serif text-xl text-ink">Chargebacks</h2>
          <p>
            Please contact us before filing a chargeback, we can almost
            always resolve billing questions faster directly. Accounts with
            an open chargeback are paused until the dispute is closed.
          </p>

          <h2 className="font-serif text-xl text-ink">Changes to this policy</h2>
          <p>
            We may update this policy as the product evolves. Material
            changes are announced by email to active subscribers at least 14
            days before they take effect. See also our{" "}
            <Link to="/terms" className="underline">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </article>
    </PublicShell>
  );
}
